/* eslint-disable complexity */
import React, { FC, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Typography from 'carbon-react/lib/components/typography';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ROUTES, StoreState, workspaceByIdSelector } from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { createImportSheet } from '../../../../../../../core/api/external-sheets';
import { TRACKING_EVENTS } from '../../../../../../../core/constants';
import { trackEvent } from '../../../../../../../core/tracker';
import { PredictManagementContext } from '../../../../../../../predict-management/context/predict-management-context';
import { usePredictionsInfoContext } from '../../../../../context/predictions-info-context';
import { useGoogleSheetsToken } from '../../../hooks/use-google-sheets-token/use-google-sheets-token';
import { getRedirectURL } from '../../../url-utils';
import { getImportFilename } from '../../../utils';

export interface Props {
  onClose: () => void;
  onAuthRedirect: () => void;
}

export const ImportModal: FC<Props> = ({ onClose, onAuthRedirect }: Props) => {
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { fetchForecast, forecast } = usePredictionsInfoContext();
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId } = useParams();

  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sheetURL, setSheetURL] = useState<string>('');
  const [createError, setCreateError] = useState<string>('');

  const importRedirectURL = getRedirectURL(workspaceId, organisationId, forecastId!, ROUTES.IMPORT_REDIRECT);
  const { result: tokenData } = useGoogleSheetsToken(importRedirectURL);

  const intl = useIntl();

  const organisationName = useSelector((store: StoreState) => {
    const workspace = workspaceByIdSelector(store.normalised, workspaceId) as WorkspacesListingItem;
    const org = workspace?.organisations.find(o => o.id === organisationId);
    return org?.name;
  });

  // Attempt to create sheet if the forecast has a token
  useEffect(() => {
    const createSheet = async () => {
      try {
        setIsLoading(true);
        const createImportSheetResult = await createImportSheet(
          workspaceId,
          organisationId,
          forecastId as string,
          getImportFilename(organisationName || '', intl, forecast?.name, forecast?.kind)
        );
        trackEvent(TRACKING_EVENTS.IMPORT_CREATE);
        createImportSheetResult?.sheet_url && setSheetURL(createImportSheetResult.sheet_url);
        fetchForecast && fetchForecast(); // reload the forecast to update import sheet details
      } catch {
        setCreateError('Failed to create import sheet');
        addErrorMessage?.(new Error('Failed to create import sheet'));
      } finally {
        setIsLoading(false);
      }
    };

    if (tokenData) {
      if (tokenData.hasToken) {
        createSheet();
      } else {
        setIsLoading(false);
      }
    }
  }, [
    addErrorMessage,
    fetchForecast,
    forecast?.kind,
    forecast?.name,
    forecastId,
    intl,
    organisationId,
    organisationName,
    tokenData,
    workspaceId
  ]);

  // Show any token errors
  useEffect(() => {
    if (tokenData?.error) {
      addErrorMessage?.(new Error(tokenData.error));
    }
  }, [addErrorMessage, tokenData?.error]);

  const handleViewImportSheet = () => {
    trackEvent(TRACKING_EVENTS.IMPORT_OPEN_TEMPLATE);
    window.open(sheetURL, '_blank');
    onClose();
  };

  const handleAuthorisationAction = async () => {
    trackEvent(TRACKING_EVENTS.IMPORT_AUTHORISE);
    window.open(tokenData?.authUrl, '_blank');
    onAuthRedirect();
    onClose();
  };

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      size="medium-small"
      data-testid="import-dialog"
      aria-label={intl.formatMessage({ id: 'predict.create_master_file', defaultMessage: 'Create master file' })}
      title={intl.formatMessage({ id: 'predict.create_master_file', defaultMessage: 'Create master file' })}
    >
      <Form
        stickyFooter
        leftSideButtons={
          !isLoading && (
            <Button disabled={isLoading} onClick={onClose} data-testid="import-dialog-actions-secondary">
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          )
        }
        saveButton={
          <>
            {sheetURL && (
              <Button
                disabled={isLoading}
                onClick={handleViewImportSheet}
                buttonType="primary"
                data-testid="import-dialog-actions-view-import-sheet"
              >
                <FormattedMessage id="predict.view_import" defaultMessage="View file" />
              </Button>
            )}
            {tokenData?.authUrl && (
              <Button
                disabled={isLoading}
                onClick={handleAuthorisationAction}
                buttonType="primary"
                data-testid="import-dialog-actions-primary"
              >
                <FormattedMessage id="common.continue" defaultMessage="Continue" />
              </Button>
            )}
          </>
        }
        onSubmit={e => e.preventDefault()}
      >
        {isLoading && <UILoader />}
        {tokenData?.authUrl && (
          <Typography fontSize="16px" mb={0} data-testid="import-dialog-auth-required">
            <FormattedMessage
              id="predict.we_create_google_sheets_document_master_file"
              defaultMessage="We will create a Google Sheets document which will be your master file. You will need to log in to your Google account.{br}{br}This will only give us access to the files and folders we have created."
              values={{
                br: <br />
              }}
            />
          </Typography>
        )}
        {sheetURL && (
          <Typography fontSize="16px" mb={0} data-testid="import-dialog-creation-complete">
            <FormattedMessage
              id="predict.master_file_successfully_created"
              defaultMessage="Your master file has been successfully created in Google Sheets."
            />
          </Typography>
        )}
        {(createError || tokenData?.error) && (
          <Typography fontSize="16px" mb={0} data-testid="import-dialog-auth-error">
            <FormattedMessage
              id="predict.issue_creating_master_file"
              defaultMessage="There has been an issue creating your master file. Please try again and contact support if it persists."
            />
          </Typography>
        )}
      </Form>
    </Dialog>
  );
};
